import React, { useState } from "react";
import { useApp, t } from "@/lib/AppContext";
import { resolveImg } from "@/lib/api";
import { Send, MessageCircle, Phone, Mail, Clock, MapPin, ChevronRight } from "lucide-react";
import SectionLabel from "@/components/SectionLabel";
import FeedbackDialog from "@/components/FeedbackDialog";

export default function Contacts() {
  const { content, settings } = useApp();
  const bg = settings?.bg_image_urls?.contacts;
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const contactCards = [
    settings?.phone && { Icon: Phone, label: "Телефон", value: settings.phone, href: `tel:${settings.phone.replace(/[^+\d]/g, "")}` },
    settings?.email && { Icon: Mail, label: "Email", value: settings.email, href: `mailto:${settings.email}` },
    settings?.address && { Icon: MapPin, label: "Адрес", value: settings.address, href: null },
    settings?.work_hours && { Icon: Clock, label: "Время работы", value: settings.work_hours, href: null },
  ].filter(Boolean);

  const hasMessengers = settings?.telegram_url || settings?.whatsapp_url || settings?.telegram_channel;
  const team = (settings?.contacts_info || []).filter(m => m.is_active !== false && m.name);
  const showContactsCol = contactCards.length > 0;

  return (
    <div data-testid="contacts-page">
      <section className="relative py-12 md:py-20 lg:py-24 section-on-dark">
        <div className="absolute inset-0">
          {bg && <img src={resolveImg(bg)} alt="" className="w-full h-full object-cover" />}
          <div className="absolute inset-0" style={{ background: "rgba(15,36,68,0.94)" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <SectionLabel light>{t(content, "contacts.label", "Контакты")}</SectionLabel>
          <h1 className="section-heading text-white mt-4">{t(content, "contacts.title", "Мы на связи")}</h1>
          <p className="text-white/75 mt-3 max-w-2xl">{t(content, "contacts.subtitle", "")}</p>
        </div>
      </section>

      <section className="py-12 md:py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`grid gap-6 md:gap-10 ${showContactsCol ? "lg:grid-cols-2" : "lg:grid-cols-1"}`}>
            {showContactsCol && (
              <div className="space-y-3 md:space-y-4" data-testid="contacts-info-block">
                {contactCards.map(({ Icon, label, value, href }, i) => (
                  <div key={i} className="flex items-start gap-3 md:gap-4 p-4 md:p-5 rounded-[var(--radius-lg)] border" style={{ borderColor: "var(--color-border)" }}>
                    <Icon size={22} className="shrink-0 mt-0.5" style={{ color: "var(--color-primary)" }} />
                    <div className="min-w-0">
                      <div className="font-accent text-xs uppercase tracking-widest text-[var(--color-text-secondary)]">{label}</div>
                      {href ? (
                        <a href={href} className="font-headings font-bold text-base md:text-lg hover:text-[var(--color-primary)] break-all">{value}</a>
                      ) : (
                        <div className="font-headings font-bold text-base md:text-lg">{value}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {hasMessengers || true ? (
              <div className="space-y-3">
                {settings?.telegram_url && (
                  <a href={settings.telegram_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-3 p-4 md:p-5 rounded-[var(--radius-lg)] text-white" style={{ background: "#229ED9" }} data-testid="contacts-tg-btn">
                    <span className="flex items-center gap-3"><Send size={20} className="md:w-5 md:h-5" /><span className="font-accent font-semibold uppercase tracking-wider text-sm md:text-base">{t(content, "contacts.btn_telegram", "Telegram")}</span></span>
                    <ChevronRight size={18} />
                  </a>
                )}
                {settings?.whatsapp_url && (
                  <a href={settings.whatsapp_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-3 p-4 md:p-5 rounded-[var(--radius-lg)] text-white" style={{ background: "#25D366" }} data-testid="contacts-wa-btn">
                    <span className="flex items-center gap-3"><MessageCircle size={20} /><span className="font-accent font-semibold uppercase tracking-wider text-sm md:text-base">{t(content, "contacts.btn_whatsapp", "WhatsApp")}</span></span>
                    <ChevronRight size={18} />
                  </a>
                )}
                {settings?.telegram_channel && (
                  <a href={settings.telegram_channel} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-3 p-4 md:p-5 rounded-[var(--radius-lg)] text-white" style={{ background: "var(--color-secondary)" }}>
                    <span className="flex items-center gap-3"><Send size={20} /><span className="font-accent font-semibold uppercase tracking-wider text-sm md:text-base">{t(content, "contacts.btn_channel", "Telegram-канал")}</span></span>
                    <ChevronRight size={18} />
                  </a>
                )}
                <button onClick={() => setFeedbackOpen(true)} className="flex items-center justify-between gap-3 p-4 md:p-5 rounded-[var(--radius-lg)] text-white w-full" style={{ background: "var(--color-primary)" }} data-testid="contacts-feedback-btn">
                  <span className="font-accent font-semibold uppercase tracking-wider text-sm md:text-base">{t(content, "feedback.contact_title", "Написать менеджеру")}</span>
                  <ChevronRight size={18} />
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />

      {team.length > 0 && (
        <section className="py-12 md:py-16 bg-[var(--color-bg-primary)]" data-testid="team-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionLabel>Наша команда</SectionLabel>
            <h2 className="section-heading mt-4 mb-8 md:mb-10">Менеджеры</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {team.map((m, i) => (
                <div key={i} className="bg-white rounded-[var(--radius-lg)] p-5 md:p-6 border-t-2 flex flex-col items-center text-center" style={{ borderColor: "var(--color-secondary)", boxShadow: "var(--shadow-card)" }} data-testid={`manager-${i}`}>
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden mb-4 bg-[var(--color-bg-primary)] flex items-center justify-center border-2" style={{ borderColor: "var(--color-gold)" }}>
                    {m.photo_url ? <img src={resolveImg(m.photo_url)} alt={m.name} className="w-full h-full object-cover" /> : <span className="font-headings font-extrabold text-2xl md:text-3xl text-[var(--color-text-secondary)]">{(m.name || "")[0]?.toUpperCase()}</span>}
                  </div>
                  <h3 className="font-headings font-bold text-base md:text-lg">{m.name}</h3>
                  {m.role && <p className="font-accent text-xs uppercase tracking-widest text-[var(--color-text-secondary)] mt-1">{m.role}</p>}
                  <div className="flex gap-2 mt-4">
                    {m.telegram_url && <a href={m.telegram_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full flex items-center justify-center text-white" style={{ background: "#229ED9" }}><Send size={16} /></a>}
                    {m.whatsapp_url && <a href={m.whatsapp_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full flex items-center justify-center text-white" style={{ background: "#25D366" }}><MessageCircle size={16} /></a>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
